import React from 'react';
import { useIntl } from 'react-intl';
import clsx from 'clsx';
import { Spinner } from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck } from '@fortawesome/pro-light-svg-icons/faCheck';
import { TypedMemo } from '../common/types';
import { teamPlanLabel } from '../team/teamAcceptMessages';
import { workspaceMessages } from './workspaceMessages';
import { workspaceInitials } from './workspaceInitials';
import type { WorkspaceSummary } from './useWorkspaces';
import styles from './WorkspaceSwitcherRow.module.scss';

interface WorkspaceSwitcherRowProps {
  workspace: WorkspaceSummary;
  variant: 'menu' | 'sheet';
  isPending: boolean;
  isDisabled: boolean;
  onSelect: (licenseId: number) => void;
}

// design: workspaces ws.css .h-*. The hue of the mark comes from the licence id, so it is stable.
const markHues = [styles.hue0, styles.hue1, styles.hue2, styles.hue3, styles.hue4, styles.hue5];

/**
 * design: workspaces a1 / b1 row. The mark, the name, "Your workspace" on the home row in place of
 * the owner email (README point 7), the plan and the artist count, the role chip (a spinner while
 * this row opens, a3) and a check on the active workspace. A click on the active row goes to
 * `onSelect` too: the section closes the menu for it (a1).
 */
const WorkspaceSwitcherRowComponent: React.FC<WorkspaceSwitcherRowProps> = ({
  workspace,
  variant,
  isPending,
  isDisabled,
  onSelect,
}) => {
  const intl = useIntl();
  const isOwner = workspace.role === 'OWNER';
  const roleLabel = isOwner
    ? intl.formatMessage({ id: 'app.sml.team.role.owner', defaultMessage: 'Owner' })
    : intl.formatMessage({ id: 'app.sml.team.role.member', defaultMessage: 'Member' });
  return (
    <button
      type="button"
      className={clsx(styles.row, variant === 'sheet' && styles.sheetRow)}
      // design: workspaces a1. Inside the dropdown the rows are menu items, as the artist rows are.
      role={variant === 'menu' ? 'menuitem' : undefined}
      disabled={isDisabled}
      aria-current={workspace.isActive ? 'true' : undefined}
      aria-busy={isPending ? 'true' : undefined}
      onClick={() => onSelect(workspace.licenseId)}
    >
      <span className={clsx(styles.mark, markHues[workspace.licenseId % markHues.length])} aria-hidden="true">
        {workspaceInitials(workspace.name)}
      </span>
      <span className={styles.text}>
        <span className={styles.name}>{workspace.name}</span>
        {workspace.isHome ? (
          <span>
            <span className={styles.homeChip}>{intl.formatMessage(workspaceMessages.yourWorkspace)}</span>
          </span>
        ) : (
          <span className={styles.meta} data-clarity-mask="true">
            {workspace.ownerEmail}
          </span>
        )}
        <span className={styles.meta}>
          {intl.formatMessage(workspaceMessages.planArtists, {
            plan: teamPlanLabel(intl, workspace.plan),
            count: workspace.artistCount,
          })}
        </span>
      </span>
      <span className={styles.end}>
        {isPending ? (
          <Spinner size="sm" className={styles.spinner}>
            {intl.formatMessage(workspaceMessages.opening)}
          </Spinner>
        ) : (
          <span className={clsx(styles.role, isOwner && styles.roleOwner)}>{roleLabel}</span>
        )}
        {workspace.isActive && <FontAwesomeIcon icon={faCheck} className={styles.check} />}
      </span>
    </button>
  );
};

export const WorkspaceSwitcherRow = TypedMemo(WorkspaceSwitcherRowComponent);
